import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Reports from audio → Import from Axon. Fixture `importFromAxon`: the
// department has Axon Evidence connected. The officer opens Add Bodycam Video,
// sees the 5 hour fleet clip disabled, searches "Valley Mart", adds two clips
// from their detail views, mixes in a local dictation, and uploads. The Axon
// transfers stay "in flight" (the fixture answers after 20 s), so the toast
// shows the background transfer. Rows are indexed by `data-file-index`.
const MODAL = sel("evidenceModal");
const LIST = sel("evidenceList");
const SEARCH_INPUT = `${MODAL} input[placeholder="Search by title..."]`;
const SEARCH_BUTTON = `${MODAL} div.flex.gap-2.mb-4 button`;
const CLOSE_X = `${MODAL} .modal-box > div.flex.justify-between button`;
const ENABLED_ITEM = `${LIST} button:not(:disabled)`;
const DISABLED_ITEM = `${LIST} button:disabled`;
// The search results are newest first (backup unit, then the officer's own
// primary camera), so "my own camera" is the second button.
const OWN_CAMERA = `${LIST} > button:nth-of-type(2)`;
const ROW_0 = '[data-file-index="0"]';
const ROW_2 = '[data-file-index="2"]';

const scene: Scene = {
  id: "import-from-axon-evidence",
  title: "Import from Axon",
  startRoute: "/cases",
  fixture: "importFromAxon",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "If your department uses Axon Evidence, you don't have to download body cam footage just to upload it again. You pick it inside the upload window, and the server pulls it straight from Axon.",
      actions: [
        { type: "waitFor", target: sel("newCaseDropdown") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "open",
      narration:
        "I'll click New Case, choose Upload Files, and click Add Bodycam Video. If you don't see that button, your admin hasn't connected Axon yet.",
      actions: [
        // "New Case" at about 0.6 s, "Upload Files" at 3.1 s, "Add Bodycam Video" at 3.6 s.
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 200 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 1200 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadBodycam") },
        { type: "moveTo", target: sel("uploadBodycam") },
        { type: "wait", ms: 900 },
        { type: "click", target: sel("uploadBodycam") },
        { type: "waitFor", target: LIST, timeoutMs: 8000 },
      ],
    },
    {
      id: "browse",
      narration:
        "This is your Axon library. Anything over four hours is grayed out, like this fleet camera clip, so it can't be added. I'll search for Valley Mart to find my footage.",
      actions: [
        // "Anything over four hours" at 2.0 s, "I'll search" at 5.8 s.
        { type: "waitFor", target: DISABLED_ITEM },
        { type: "wait", ms: 1900 },
        { type: "moveTo", target: DISABLED_ITEM },
        { type: "spotlight", target: DISABLED_ITEM },
        { type: "wait", ms: 3300 },
        { type: "spotlight", target: null },
        { type: "moveTo", target: SEARCH_INPUT },
        { type: "type", target: SEARCH_INPUT, text: "Valley Mart", cps: 14 },
        { type: "click", target: SEARCH_BUTTON },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "add",
      narration: "Click a clip to see its details, then click Add to Upload. I'll add my own camera first.",
      actions: [
        // Opens the clip at about 0.8 s, "Add to Upload" at 2.2 s.
        { type: "waitFor", target: OWN_CAMERA },
        { type: "moveTo", target: OWN_CAMERA },
        { type: "wait", ms: 100 },
        { type: "click", target: OWN_CAMERA },
        { type: "waitFor", target: sel("evidenceAdd") },
        { type: "moveTo", target: sel("evidenceAdd") },
        { type: "wait", ms: 1000 },
        { type: "click", target: sel("evidenceAdd") },
        { type: "waitFor", target: sel("uploadFileRow") },
        { type: "wait", ms: 500 },
      ],
    },
    {
      id: "second",
      narration:
        "Let me open Add Bodycam Video again and add the backup unit too. A clip I've already added shows as Added, so I can't pick it twice.",
      actions: [
        // Open at 0.9 s, add the backup unit by 4.2 s, reopen for "shows as Added" at 6.0 s.
        { type: "wait", ms: 400 },
        { type: "click", target: sel("uploadBodycam") },
        { type: "waitFor", target: ENABLED_ITEM },
        { type: "wait", ms: 700 },
        { type: "click", target: ENABLED_ITEM },
        { type: "waitFor", target: sel("evidenceAdd") },
        { type: "wait", ms: 400 },
        { type: "click", target: sel("evidenceAdd") },
        { type: "waitFor", target: '[data-file-index="1"]' },
        { type: "wait", ms: 600 },
        { type: "click", target: sel("uploadBodycam") },
        { type: "waitFor", target: DISABLED_ITEM },
        { type: "moveTo", target: DISABLED_ITEM },
        { type: "spotlight", target: LIST },
        { type: "wait", ms: 1700 },
        { type: "spotlight", target: null },
        { type: "click", target: CLOSE_X },
        { type: "wait", ms: 300 },
      ],
    },
    {
      id: "mix",
      narration:
        "You can mix in files from your computer as well. I'll add my dictation. Body cam rows are always processed as Interaction, so there's nothing to set for them.",
      actions: [
        // "I'll add my dictation" at 2.6 s, "Body cam rows" at 4.2 s.
        { type: "moveTo", target: sel("uploadDropzone") },
        { type: "wait", ms: 1700 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/narrative-retail-theft.mp3`,
          name: "narrative-retail-theft.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: ROW_2 },
        { type: "scrollIntoView", target: ROW_0 },
        { type: "wait", ms: 700 },
        { type: "moveTo", target: ROW_0 },
        { type: "spotlight", target: ROW_0 },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "upload",
      narration:
        "When I click Upload, my dictation goes up from the browser, and both Axon clips start transferring on the server at the same time.",
      actions: [
        // "When I click Upload" at 0.0 to 1.4 s.
        { type: "scrollIntoView", target: sel("uploadSubmit") },
        { type: "moveTo", target: sel("uploadSubmit") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("uploadSubmit") },
        { type: "waitFor", target: sel("uploadToast"), timeoutMs: 15000 },
      ],
    },
    {
      id: "leave",
      narration:
        "Once the local file finishes, you can close the tab and the transfers keep going. The toast shows how many Axon files are still on the way, and the draft starts when everything has been transcribed.",
      actions: [
        // "The toast shows" at 4.8 s.
        { type: "waitFor", target: sel("uploadToast") },
        { type: "wait", ms: 4600 },
        { type: "moveTo", target: sel("uploadToast") },
        { type: "spotlight", target: sel("uploadToast") },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
